import { Button, Flex, FormControl, FormLabel, FormErrorMessage, Box, Tooltip, CloseButton, Select } from "@chakra-ui/react";
import { FunctionComponent, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { CreateOrUpdateBenchmarkReference, BenchmarkReference } from "../../../api/benchmark/benchmarkApi";
import {
    useCreateBenchmarkReference,
    useDeleteBenchmarkReference,
    useGetBenchmarkOutputs,
    useUpdateBenchmarkReference,
} from "../../../api/benchmark/benchmarkQueries";
import { ConfirmModal } from "../../ClientAdminPage/EntityPanel/ConfirmModal";
import { FileContainer, FileUploader, SingleSelectControlled } from "am-tax-fe-core";
import { ALLOWED_BENCHMARK_EXTENSIONS, getRegionLabel, getRegionValue, Region, REGIONS, SERVICE_TYPES } from "../../../utils/constants";

interface BenchmarkReferenceFormProps {
    show: boolean;
    onClose: () => void;
    rowData?: BenchmarkReference;
    onSave?: (data: BenchmarkReference) => void;
}

type FormFields = {
    region: string;
    serviceType: string;
    benchmarkReference: string;
};

export const BenchmarkReferenceForm: FunctionComponent<BenchmarkReferenceFormProps> = ({ onClose, rowData }) => {
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
    const [hasBenchmarkFile, setHasBenchmarkFile] = useState<boolean>(!!rowData?.blobStorageLink && rowData?.blobStorageLink?.split("/").length > 1);
    const [selectedBenchmarkFile, setSelectedBenchmarkFile] = useState<FileContainer | undefined>();
    const isEdit: boolean = !!rowData;

    const formValues: FormFields = {
        region: rowData?.region ? getRegionValue(rowData.region) ?? rowData.region : "",
        serviceType: rowData?.serviceType || "",
        benchmarkReference: rowData?.blobStorageLink || "",
    };

    const {
        control,
        handleSubmit,
        register,
        formState: { errors },
        setValue,
        clearErrors,
        setError,
    } = useForm<FormFields>({ values: formValues });

    const { mutateAsync: updateBenchmarkReferenceMutation, isPending: isUpdatingBenchmarkReference } = useUpdateBenchmarkReference();
    const { mutateAsync: createBenchmarkReferenceMutation, isPending: isCreatingBenchmarkReference } = useCreateBenchmarkReference();
    const { mutateAsync: deleteBenchmarkReferenceMutation, isPending: isDeletingBenchmarkReference } = useDeleteBenchmarkReference();
    const { data: benchmarkOutputs, isLoading: isBenchmarkOutputsLoading } = useGetBenchmarkOutputs();
    const isSavingBenchmarkReference = isUpdatingBenchmarkReference || isCreatingBenchmarkReference;

    // TODO: Add error handling for all of these mutations
    const onSubmit: SubmitHandler<FormFields> = async data => {
        const benchmarkReferenceId = rowData?.id;
        const benchmarkReferenceArgs: CreateOrUpdateBenchmarkReference = {
            Id: benchmarkReferenceId,
            Region: data.region as Region,
            ServiceType: data.serviceType,
            BlobStorageLink: data.benchmarkReference,
        };

        if (isEdit) {
            await updateBenchmarkReferenceMutation({ data: benchmarkReferenceArgs, file: selectedBenchmarkFile });
        } else {
            if (!selectedBenchmarkFile && data.benchmarkReference === "") {
                setError("benchmarkReference", {
                    type: "manual",
                    message: "Benchmark reference file is required.",
                });
                return;
            }

            await createBenchmarkReferenceMutation({ benchmarkReference: benchmarkReferenceArgs, file: selectedBenchmarkFile });
        }
        onClose();
    };

    const handleRemoveBenchmarkReference = () => {
        setSelectedBenchmarkFile(undefined);
        setHasBenchmarkFile(false);
        setValue("benchmarkReference", "");
        // This will trigger validation since the field is required
        setError("benchmarkReference", {
            type: "manual",
            message: "Benchmark reference file is required",
        });
    };

    const handleDelete = async () => {
        if (rowData) {
            await deleteBenchmarkReferenceMutation(rowData?.id || "");
            onClose();
        }
    };

    const closeConfirmModal = () => {
        setShowConfirmModal(false);
    };

    const onFileAdded = async (fileContainer: FileContainer) => {
        const fileName = fileContainer.file.name;
        const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";

        if (ALLOWED_BENCHMARK_EXTENSIONS.includes(fileExtension)) {
            setSelectedBenchmarkFile(fileContainer);
            setValue("benchmarkReference", fileContainer.file.name);
            clearErrors("benchmarkReference");
        } else {
            setSelectedBenchmarkFile(undefined);
            setValue("benchmarkReference", "");
            setError("benchmarkReference", {
                type: "manual",
                message: "Invalid file format.",
            });
        }
    };

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Flex gap="2rem" flexFlow="column nowrap">
                    <Flex flexDirection="column" gap={4}>
                        <FormControl isRequired isInvalid={!!errors.region}>
                            <FormLabel>Region</FormLabel>
                            <Select {...register("region", { required: "Region is required" })} placeholder="Select region">
                                {REGIONS.map(region => (
                                    <option key={region} value={region}>
                                        {getRegionLabel(region)}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.region?.message}</FormErrorMessage>
                        </FormControl>
                        <FormControl isRequired isInvalid={!!errors.serviceType}>
                            <FormLabel>Service Type</FormLabel>
                            <Select {...register("serviceType", { required: "Service type is required" })} placeholder="Select service type">
                                {SERVICE_TYPES.map(service => (
                                    <option key={service} value={service}>
                                        {service}
                                    </option>
                                ))}
                            </Select>
                            <FormErrorMessage>{errors.serviceType?.message}</FormErrorMessage>
                        </FormControl>

                        <Flex mt="1.5rem" mb="0.5rem" gap="0.5rem">
                            <Button variant={selectedSectionIndex === 0 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(0)}>
                                Upload Benchmark Reference
                            </Button>
                            <Button variant={selectedSectionIndex === 1 ? "solid" : "outline"} onClick={() => setSelectedSectionIndex(1)}>
                                Select Benchmark Reference from Library
                            </Button>
                        </Flex>

                        {selectedSectionIndex === 0 ? (
                            <>
                                {hasBenchmarkFile ? (
                                    <Box>
                                        <FormLabel>Benchmark Reference File:</FormLabel>
                                        <Flex align="center" mb="1rem">
                                            <p>{rowData?.blobStorageLink?.split("/").pop()}</p>
                                            <Tooltip label="Remove org diagram">
                                                <CloseButton ml="1rem" variant="ghost" aria-label="Close" onClick={() => handleRemoveBenchmarkReference()} />
                                            </Tooltip>
                                        </Flex>
                                    </Box>
                                ) : (
                                    <Box>
                                        <FormControl isInvalid={!!errors?.benchmarkReference}>
                                            <FormLabel>Upload Benchmark Reference:</FormLabel>
                                            <Flex direction="column" gap={5}>
                                                <FileUploader
                                                    {...register("benchmarkReference")}
                                                    maxSize={10485760}
                                                    maxFiles={1}
                                                    onFileAdded={onFileAdded}
                                                    accept={{
                                                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": ALLOWED_BENCHMARK_EXTENSIONS,
                                                    }}
                                                />
                                            </Flex>
                                            <FormErrorMessage>{errors.benchmarkReference?.message}</FormErrorMessage>
                                        </FormControl>
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box>
                                <FormControl isInvalid={!!errors.benchmarkReference}>
                                    <FormLabel>Select Benchmark Reference from Library:</FormLabel>
                                    <Box>
                                        <SingleSelectControlled
                                            control={control}
                                            disabled={isBenchmarkOutputsLoading}
                                            options={benchmarkOutputs
                                                ?.filter(output => output.blobStorageLink)
                                                .map(output => ({
                                                    value: output.id,
                                                    label: output.blobStorageLink?.split("/").pop() || "Unnamed Benchmark Output",
                                                }))}
                                            name={"benchmarkReference"}
                                        />
                                    </Box>
                                    <FormErrorMessage>{errors.benchmarkReference?.message}</FormErrorMessage>
                                </FormControl>
                            </Box>
                        )}
                    </Flex>
                </Flex>
                <Flex gap="2rem" flexFlow="column nowrap" mt="2rem" mb="1rem">
                    <Flex w="100%" justify="space-between" align="center">
                        {isEdit ? (
                            <Button
                                variant="ghost"
                                colorScheme="red"
                                onClick={() => setShowConfirmModal(true)}
                                isLoading={isDeletingBenchmarkReference}
                                isDisabled={isDeletingBenchmarkReference}
                            >
                                Delete Benchmark Reference
                            </Button>
                        ) : (
                            <span />
                        )}
                        <Flex gap={2}>
                            <Button onClick={onClose} mr={3} variant="ghost">
                                Cancel
                            </Button>
                            <Button type="submit" variant="primary" isLoading={isSavingBenchmarkReference} isDisabled={isSavingBenchmarkReference}>
                                {rowData ? "Update" : "Add"}
                            </Button>
                        </Flex>
                    </Flex>
                </Flex>
            </form>
            <ConfirmModal
                show={showConfirmModal}
                handleDelete={handleDelete}
                onClose={closeConfirmModal}
                isDeletingEntity={isDeletingBenchmarkReference}
                bodyText="Deleting this benchmark reference will also delete any associated data."
            />
        </>
    );
};
